import React from "react";
import { useSelector } from "react-redux";
import { EyeOutlined } from "@ant-design/icons";
import { parseReferenceLinks } from "../../helpers/referenceLinks";
import ExpandableText from "../../components/ExpandableText/ExpandableText";

const formatDisplayAmount = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const numericValue = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(numericValue)) return String(value);
  return numericValue.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const getFileNameFromUrl = (url) => {
  if (!url || typeof url !== "string") return "Document";
  const lastPart = url.split("/").pop() || "Document";
  return decodeURIComponent(lastPart);
};

const DealOverview = ({ deal }) => {
  const { allDealsListLoading: loading = false } = useSelector(
    (state) => state.dealApproval || {},
  );

  const finalizedDocs = [
    {
      key: "pitchDoc",
      label: "Finalized Document",
      url: deal?.pitchDoc,
    },
  ].filter((doc) => Boolean(doc.url));

  const referenceLinks = parseReferenceLinks(
    deal?.referenceLinks,
  ).filter((entry) => entry?.url?.trim());

  return (
    <div className="jv-view-page">
      <div className="jv-view-header-main">
        <div>
          <h2>Joint Venture Deal View - {deal?.dealId || "N/A"}</h2>
          <div className="header-subline">
            <span className="deal-chip">Joint Ventures</span>
            <span>
              Read-only view of submitted and approved JV deal details
            </span>
          </div>
        </div>
        <span className="status-pill">{deal?.dealStatus || "N/A"}</span>
      </div>

      {loading ? (
        <p className="panel-subtext">Loading deal details...</p>
      ) : null}

      <section className="panel-card">
        <h3>Joint Venture Details</h3>
        <div className="details-grid">
          <div>
            <label>Project Name</label>
            <p>{deal?.dealName || "N/A"}</p>
          </div>
          <div>
            <label>Project Duration</label>
            <p>{deal?.dealDuration || "N/A"} Months</p>
          </div>
          <div>
            <label>Project Value</label>
            <p>{formatDisplayAmount(deal?.dealValue)}</p>
          </div>
          <div>
            <label>Fund Required</label>
            <p>{formatDisplayAmount(deal?.fundRequired)}</p>
          </div>
          <div>
            <label>Platform Fee</label>
            {/* <p>{deal?.platformFee ?? "3%"}</p> */}
            <p>3%</p>
          </div>
        </div>
      </section>

      <section className="panel-card">
        <h3>Approved Fund Details</h3>
        {/* <p className="panel-subtext">Review approved funding values captured for this deal</p> */}

        <div className="details-grid approved-grid">
          <div>
            <label>Approved Fund Amount</label>
            <p>{formatDisplayAmount(deal?.approvedFundAmt)}</p>
          </div>
          <div>
            <label>Minimum Investment Criteria</label>
            <p>{formatDisplayAmount(deal?.minDealAmt)}</p>
          </div>
          <div>
            <label>Expected ROI (%)</label>
            <p>{deal?.expectedRoi ?? "N/A"}</p>
          </div>
          <div>
            <label>Profit Share (%)</label>
            <p>{deal?.profitShare ?? "N/A"}</p>
          </div>
        </div>
      </section>

      {referenceLinks.length > 0 && (
				<section className="panel-card">
					<h3>Additional Reference Links</h3>
					<p className="panel-subtext">Reference links and descriptions for this deal</p>

					<div className="reference-links-readonly">
						{referenceLinks.map((entry, index) => (
							<div key={`${entry.url}-${index}`} className="reference-link-card">
								<div>
									<label>Reference Link</label>
									<a href={entry.url} target="_blank" rel="noopener noreferrer">
										{entry.url}
									</a>
								</div>

								{entry.description ? (
									<div>
										<label>Link Description</label>
										<ExpandableText
											text={entry.description}
											className="reference-link-description"
											textClassName="reference-link-description-text"
										/>
									</div>
								) : null}
							</div>
						))}
					</div>
				</section>
			)}

      <section className="panel-card">
        <h3>Finalized Documents</h3>
        <p className="panel-subtext">Final files shared during approval flow</p>
        <div className="docs-box">
          <EyeOutlined className="docs-icon" />
          {finalizedDocs.length === 0 ? (
            <p className="empty-text">No finalized documents available</p>
          ) : (
            <div className="uploaded-file-list">
              {finalizedDocs.map((doc) => (
                <p key={doc.key}>
                  <a href={doc.url} target="_blank" rel="noreferrer">
                    {doc.label}: {getFileNameFromUrl(doc.url)}
                  </a>
                </p>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="panel-card">
        <h3>Comments &amp; Notes</h3>
        <div className="readonly-notes-box">
          {deal?.comments || "No comments added"}
        </div>
      </section>
    </div>
  );
};

export default DealOverview;
